"use client";

import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { cn } from "@utils/utils";
import styles from "./Accordion.module.scss";
import assetLoader from "@utils/internalImageLoder";

interface AccordionProps {
  type?: "single" | "multiple";
  defaultValue?: string | string[];
  className?: string;
  children: React.ReactNode;
}

interface AccordionContextProps {
  expanded: Record<string, boolean>;
  toggleItem: (value: string) => void;
  type: "single" | "multiple";
}

const AccordionContext = React.createContext<AccordionContextProps | undefined>(
  undefined
);

export function Accordion({
  type = "single",
  defaultValue = [],
  className,
  children,
  ...props
}: AccordionProps) {
  // Initialize state based on defaultValue
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    if (Array.isArray(defaultValue) && defaultValue.length > 0) {
      return defaultValue.reduce((acc, value) => ({ ...acc, [value]: true }), {});
    } else if (typeof defaultValue === "string" && defaultValue) {
      return { [defaultValue]: true };
    }
    return {};
  });
  
  const toggleItem = (value: string) => {
    setExpanded((prev) => {
      if (type === "single") {
        return { [value]: !prev[value] };
      }
      return { ...prev, [value]: !prev[value] };
    });
  };

  return (
    <AccordionContext.Provider value={{ expanded, toggleItem, type }}>
      <div className={cn(styles.accordionRoot, className)} {...props}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

interface AccordionItemProps {
  value: string;
  className?: string;
  children: React.ReactNode;
}

export function AccordionItem({
  value,
  className,
  children,
  ...props
}: AccordionItemProps) {
  return (
    <div className={cn(styles.accordionItem, className)} {...props}>
      {children}
    </div>
  );
}

interface AccordionTriggerProps {
  value: string;
  className?: string;
  children: React.ReactNode;
  link?: string;
  theme?: "dark" | "light";
  iconWidth?: number;
  iconHeight?: number;
}

export function AccordionTrigger({
  value,
  className,
  children,
  link,
  theme,
  iconWidth,
  iconHeight,
  ...props
}: AccordionTriggerProps) {
  const context = React.useContext(AccordionContext);
  if (!context) {
    throw new Error("AccordionTrigger must be used within an Accordion");
  }

  const { expanded, toggleItem } = context;
  const isExpanded = expanded[value];

  if (link) {
    return (
      <div className={cn(styles.accordionTrigger, className)} {...props}>
        <Link href={link} className={styles.accordionTriggerLink} prefetch={false}>
          {children}
        </Link>
        <button
          type="button"
          className={styles.accordionTriggerIconButton}
          onClick={() => toggleItem(value)}
          aria-expanded={isExpanded}
          aria-controls={`accordion-content-${value}`}
          aria-label={isExpanded ? "Collapse section" : "Expand section"}
        >
          <Image
            src={`${assetLoader()}/icons/${theme === "dark" ? "Chevron_down_dark" : "chevron_down_light"}.svg`}
            alt=""
            width={iconWidth ?? 20}
            height={iconHeight ?? 20}
            className={styles.accordionIcon}
            aria-hidden="true"
          />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={cn(styles.accordionTrigger, className)}
      onClick={() => toggleItem(value)}
      aria-expanded={isExpanded}
      aria-controls={`accordion-content-${value}`}
      {...props}
    >
      {children}
      <Image
        src={`${assetLoader()}/icons/${theme === "dark" ? "Chevron_down_dark" : "chevron_down_light"}.svg`}
        alt="Toggle"
        width={iconWidth ?? 20}
        height={iconHeight ?? 20}
        className={styles.accordionIcon}
        aria-hidden="true"
      />
    </button>
  );
}

interface AccordionContentProps {
  value: string;
  className?: string;
  children: React.ReactNode;
}

export function AccordionContent({
  value,
  className,
  children,
  ...props
}: AccordionContentProps) {
  const context = React.useContext(AccordionContext);
  if (!context) {
    throw new Error("AccordionContent must be used within an Accordion");
  }

  const { expanded } = context;
  const isExpanded = expanded[value];

  return (
    <div
      id={`accordion-content-${value}`}
      className={cn(
        styles.accordionContent,
        styles.transitionEnabled,
        isExpanded ? styles.expanded : styles.collapsed,
        className
      )}
      aria-hidden={!isExpanded}
      {...props}
    >
      <div className={cn(
        styles.accordionContentInner,
        isExpanded ? styles.visible : styles.invisible
        )}>
        {children}
      </div>
    </div>
  );
}
